import * as Core from '@skylab-shared/core';
import { CompanyParametersModel, HistoricalEntityModel, PersonKind } from '@skylab-shared/dataModel';
import { propertyChangedJoinPointFilter as propertyJoinPointFilter } from '../common/property-changed/property-changed-filter';
import { NipValidator } from '../common/validators/nip-validator';
import { VatReleaseReasonItem, VatReleaseReasonQuery } from '../vat/queries/vat-release-reason.query-base';
import { InvalidKrsNumberLengthError } from './violations/invalid-krs-number-length-error';
import { InvalidOtherSellerNameError } from './violations/invalid-other-seller-name-error';
import { InvalidNipWarning } from './violations/invalid-other-seller-nip-warning';
import { InvalidOtherSellerRoleError } from './violations/invalid-other-seller-role-error';
import { InvalidOtherSellerTinError } from './violations/invalid-other-seller-tin-error';
import { InvalidTaxpayerDateOfBirthError } from './violations/invalid-taxpayer-date-of-birth-error';
import { InvalidTaxpayerFirstNameError } from './violations/invalid-taxpayer-first-name-error';
import { InvalidTaxpayerSurnameError } from './violations/invalid-taxpayer-surname-error';
import { InvalidVatReleaseReasonCustomNameError } from './violations/invalid-vat-release-reason-custom-name-error';

@Core.BO({ modelType: CompanyParametersModel })
export class CompanyParametersBO extends Core.BusinessObjectBase<CompanyParametersModel> {
    public constructor(
        weaver: Core.Weaver,
    ) {
        super(weaver);
    }

    protected override async initializeAsync(): Promise<void> {
        await super.initializeAsync();
        await Promise.resolve([
            this.localContext.preload(VatReleaseReasonQuery),
        ]);

        this.registerOnPropertyChanged();
        this.registerReadonlyRules();
        this.registerValidationRules();
    }

    private registerReadonlyRules(): void {
        this.readonly.addRule(
            [[HistoricalEntityModel, 'issueDocumentsToOtherSeller']],
            [
                [HistoricalEntityModel, 'name'],
                [HistoricalEntityModel, 'roleRelativeToOtherSeller'],
                [HistoricalEntityModel, 'tin'],
                [HistoricalEntityModel, 'zipCode'],
                [HistoricalEntityModel, 'countryId'],
                [HistoricalEntityModel, 'address'],
                [HistoricalEntityModel, 'city']
            ],
            () => this.model.otherSeller ? !this.model.otherSeller.issueDocumentsToOtherSeller : true
        );

        this.readonly.addRule(
            [[CompanyParametersModel, 'vatPayer']],
            [[CompanyParametersModel, 'cashBasis']],
            () => !this.model.vatPayer
        );
    }

    private registerValidationRules(): void {
        this.validation.addRule(
            [[HistoricalEntityModel, 'name']],
            ctx => {
                if (this.model.otherSeller && this.model.otherSeller.issueDocumentsToOtherSeller) {
                    return !!ctx.source.name?.trim();
                }
                return true;
            },
            InvalidOtherSellerNameError
        );

        this.registerListening(this.weaver.register(Core.PropertyChangedJoinPoint, propertyJoinPointFilter(this)(HistoricalEntityModel, ['issueDocumentsToOtherSeller'])(
            () => this.validation.queueValidation([HistoricalEntityModel, 'name'])
        )));


        this.validation.addRule(
            [[HistoricalEntityModel, 'tin']],
            ctx => {
                if (this.model.otherSeller && this.model.otherSeller.issueDocumentsToOtherSeller) {
                    return !!ctx.source.tin?.trim();
                }
                return true;
            },
            InvalidOtherSellerTinError
        );

        this.registerListening(this.weaver.register(Core.PropertyChangedJoinPoint, propertyJoinPointFilter(this)(HistoricalEntityModel, ['issueDocumentsToOtherSeller'])(
            () => this.validation.queueValidation([HistoricalEntityModel, 'tin'])
        )));

        this.validation.addRule(
            [[HistoricalEntityModel, 'tin']],
            ctx => {
                if (ctx.source.tin && this.model.otherSeller && this.model.otherSeller.issueDocumentsToOtherSeller) {
                    return NipValidator.isValid(ctx.source.tin);
                }
                return true;
            },
            InvalidNipWarning
        );

        this.registerListening(this.weaver.register(Core.PropertyChangedJoinPoint, propertyJoinPointFilter(this)(CompanyParametersModel, ['otherSeller'])(
            () => this.validation.queueValidation([HistoricalEntityModel, 'tin'])
        )));

        this.validation.addRule(
            [[HistoricalEntityModel, 'roleRelativeToOtherSeller']],
            ctx => {
                if (this.model.otherSeller && this.model.otherSeller.issueDocumentsToOtherSeller) {
                    return ctx.source.roleRelativeToOtherSeller !== null;
                }
                return true;
            },
            InvalidOtherSellerRoleError
        );

        this.registerListening(this.weaver.register(Core.PropertyChangedJoinPoint, propertyJoinPointFilter(this)(HistoricalEntityModel, ['issueDocumentsToOtherSeller'])(
            () => this.validation.queueValidation([HistoricalEntityModel, 'roleRelativeToOtherSeller'])
        )));

        this.validation.addRule(
            [[CompanyParametersModel, 'vatReleaseReasonCustomName'], [CompanyParametersModel, 'vatReleaseReasonId']],
            [[CompanyParametersModel, 'vatReleaseReasonCustomName']],
            ctx => {
                const vatReleaseReasons: VatReleaseReasonItem[] = ctx.bo.localContext.getPreloaded(VatReleaseReasonQuery)!;
                if (ctx.source.vatReleaseReasonId === vatReleaseReasons.filter(x => x.customReleaseName)[0].id)
                    return (ctx.source.vatReleaseReasonCustomName?.length ?? 0) > 0;
                return true;
            },
            InvalidVatReleaseReasonCustomNameError
        );

        this.validation.addRule(
            [[CompanyParametersModel, 'personKind'], [CompanyParametersModel, 'taxpayerFirstName']],
            [[CompanyParametersModel, 'taxpayerFirstName']],
            ctx => ctx.source.personKind === null
                || ctx.source.personKind === PersonKind.Juridical
                || (ctx.source.personKind === PersonKind.Natural && !!ctx.source.taxpayerFirstName),
            InvalidTaxpayerFirstNameError
        );

        this.validation.addRule(
            [[CompanyParametersModel, 'personKind'], [CompanyParametersModel, 'taxpayerSurname']],
            [[CompanyParametersModel, 'taxpayerSurname']],
            ctx => ctx.source.personKind === null
                || ctx.source.personKind === PersonKind.Juridical
                || (ctx.source.personKind === PersonKind.Natural && !!ctx.source.taxpayerSurname),
            InvalidTaxpayerSurnameError
        );

        this.validation.addRule(
            [[CompanyParametersModel, 'personKind'], [CompanyParametersModel, 'taxpayerDateOfBirth']],
            [[CompanyParametersModel, 'taxpayerDateOfBirth']],
            ctx => ctx.source.personKind === null
                || ctx.source.personKind === PersonKind.Juridical
                || (ctx.source.personKind === PersonKind.Natural && ctx.source.taxpayerDateOfBirth != null),
            InvalidTaxpayerDateOfBirthError
        );

        this.validation.addRule(
            [[CompanyParametersModel, 'krsNumber']],
            [[CompanyParametersModel, 'krsNumber']],
            ctx => !ctx.bo.model.krsNumber || /^\d{10}$/.test(ctx.bo.model.krsNumber),
            InvalidKrsNumberLengthError
        );
    }

    private registerOnPropertyChanged(): void {
        const domainPropertyFilter = propertyJoinPointFilter(this);
        const companyPropertiesFilter = (props: '*' | (keyof CompanyParametersModel)[]) => domainPropertyFilter(CompanyParametersModel, props);

        this.registerListening(this.weaver.register(Core.PropertyChangedJoinPoint, companyPropertiesFilter(['vatPayer'])(
            () => {
                this.model.cashBasis = false;
            }
        )));

        this.registerListening(this.weaver.register(Core.PropertyChangedJoinPoint, companyPropertiesFilter(['krsNumber'])(
            () => {
                this.model.printKrsNumber = !!this.model.krsNumber;
            }
        )));

        this.registerListening(this.weaver.register(Core.PropertyChangedJoinPoint, companyPropertiesFilter(['bdoNumber'])(
            () => {
                this.model.printBdoNumber = !!this.model.bdoNumber;
            }
        )));
    }
}
